import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Mail, CheckCircle, AlertCircle, Loader,
  UserPlus, KeyRound, Eye, EyeOff, ChevronDown, Check, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { PhoneInput } from '../components/PhoneInput';
import { useLanguage } from '../contexts/LanguageContext';
import { getCountryOptions, getCountryLabel } from '../countries';
import FieldError from '../components/FieldError';
import { isValidEmail, isValidUrl, isValidPhoneDigits, normalizeUrl } from '../lib/validation';
import { checkPasswordRules } from '../lib/passwordPolicy';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-blue-600' : 'text-red-500'}`}>
    {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
    <span>{label}</span>
  </div>
);

const BlueZoneRegistration = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('unfccc');

  // Countries list — localized labels, canonical codes
  const countryOptions = getCountryOptions(language);

  // UNFCCC tab state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [serverCode, setServerCode] = useState('');
  
  // Invitation token handling
  const [invitationToken, setInvitationToken] = useState(null);
  const [tokenValidating, setTokenValidating] = useState(false);
  
  // Check for invitation token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (token) {
      setTokenValidating(true);
      axios.get(`${API}/ultra-admin/unfccc-invitations/validate/${token}`)
        .then(r => {
          setInvitationToken(token);
          setEmail(r.data.email);
          setActiveTab('unfccc'); // Force UNFCCC tab
          setEmailVerified(false);
          // Auto-trigger verification after setting email
          setTimeout(() => {
            handleVerifyEmail();
          }, 500);
        })
        .catch(err => {
          const msg = err?.response?.data?.detail || (tr ? 'Davet linki geçersiz veya süresi dolmuş.' : 'Invitation link is invalid or expired.');
          setEmailError(msg);
          setActiveTab('unfccc');
        })
        .finally(() => setTokenValidating(false));
    }
    // eslint-disable-next-line
  }, []);

  // Self-register tab state
  const [regForm, setRegForm] = useState({
    full_name: '', email: '', password: '', password2: '',
    country_code: '', organization: '', title: '',
    phone_code: '+90', phone_number: '', website: '', address: ''
  });
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [pass2Touched, setPass2Touched] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);

  // Per-field validation state
  const [regEmailTouched,   setRegEmailTouched]   = useState(false);
  const [regPhoneTouched,   setRegPhoneTouched]   = useState(false);
  const [regWebsiteTouched, setRegWebsiteTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (k) => setFieldErrors(prev => { if (!prev[k]) return prev; const n = {...prev}; delete n[k]; return n; });
  const regEmailValid   = !regForm.email   || isValidEmail(regForm.email);
  const regPhoneValid   = !regForm.phone_number || isValidPhoneDigits(regForm.phone_number, { min: 7, max: 15 });
  const regWebsiteValid = !regForm.website || isValidUrl(regForm.website);

  // Registration open/closed check
  const [regOpen, setRegOpen] = useState(null); // null = loading

  useEffect(() => {
    axios.get(`${API}/auth/registration-status`)
      .then(r => setRegOpen(r.data.blue_zone_open !== false))
      .catch(() => setRegOpen(true));
  }, []);

  const passwordRules = checkPasswordRules(regForm.password);
  const allRulesOk = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = regForm.password === regForm.password2;

  // Filtered countries (by localized label)
  const filteredCountries = countryOptions.filter(c =>
    c.label.toLowerCase().includes(countrySearch.toLowerCase())
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && emailVerified) {
      setCanResend(true);
    }
  }, [countdown, emailVerified]);

  // Close country dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-country-dropdown]')) setCountryOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const validateEmail = (e) => isValidEmail(e);

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError(tr ? 'Geçerli bir e-posta giriniz' : 'Please enter a valid email');
    } else setEmailError('');
  };

  const handleVerifyEmail = async () => {
    if (!validateEmail(email)) {
      setEmailError(tr ? 'Geçerli bir e-posta giriniz' : 'Please enter a valid email');
      return;
    }
    setLoading(true);
    setEmailError('');
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, language });
      setEmailVerified(true);
      setCountdown(120);
      setCanResend(false);
      setServerCode(response.data.code);
    } catch (error) {
      if (error.response?.status === 404) {
        setEmailError(tr
          ? 'Bu e-posta delege veri tabanında bulunamadı. Lütfen kaydınızı kontrol ediniz.'
          : 'Email not found in the delegate database. Please check your registration.');
      } else {
        setEmailError(error.response?.data?.detail || (tr ? 'Bir hata oluştu' : 'An error occurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setCanResend(false);
    setCountdown(120);
    setVerificationCode('');
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, language });
      setServerCode(response.data.code);
    } catch {}
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setCodeError(tr ? '6 haneli kodu giriniz' : 'Please enter 6-digit code');
      return;
    }
    setLoading(true);
    setCodeError('');
    try {
      const response = await axios.post(`${API}/auth/verify-code`, { email, code: verificationCode });
      localStorage.setItem('cop31_token', response.data.access_token);
      localStorage.setItem('cop31_user', JSON.stringify(response.data));
      const slug = response.data.tenant_slug || 'default';
      navigate(`/musteri-admin/${slug}/layout`);
    } catch (error) {
      setCodeError(error.response?.data?.detail || (tr ? 'Geçersiz kod' : 'Invalid code'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelfRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    // Touch all validated fields so errors surface
    setRegEmailTouched(true);
    setRegPhoneTouched(true);
    setRegWebsiteTouched(true);

    // Collect per-field required / format errors
    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (!regForm.full_name.trim()) errs.full_name = reqMsg;
    if (!regForm.email.trim())     errs.email    = reqMsg;
    else if (!isValidEmail(regForm.email)) errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Please enter a valid email.';
    if (!regForm.country_code)     errs.country_code = tr ? 'Lütfen ülkenizi seçiniz.'   : 'Please select your country.';
    if (!regForm.organization.trim()) errs.organization = reqMsg;
    if (!regForm.password)         errs.password = reqMsg;
    else if (!allRulesOk)          errs.password = tr ? 'Şifre tüm kurallara uymalıdır.' : 'Password must meet all rules.';
    if (!regForm.password2)        errs.password2 = reqMsg;
    else if (!passwordsMatch)      errs.password2 = tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.';
    if (regForm.phone_number && !regPhoneValid) {
      errs.phone_number = tr ? 'Telefon 7–15 rakam içermelidir.' : 'Phone must be 7–15 digits.';
    }
    if (regForm.website && !regWebsiteValid) {
      errs.website = tr
        ? 'Geçerli bir site giriniz (örn. https://firmaniz.com).'
        : 'Enter a valid site (e.g. https://yourcompany.com).';
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      if (errs.password) setPasswordTouched(true);
      // Show concise summary on the top banner as well
      setRegError(tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.');
      return;
    }
    const phoneComposed = regForm.phone_number
      ? `${regForm.phone_code} ${regForm.phone_number}`
      : '';
    const websiteNormalized = normalizeUrl(regForm.website);

    setRegLoading(true);
    try {
      await axios.post(`${API}/auth/self-register`, {
        email: regForm.email,
        full_name: regForm.full_name,
        country_code: regForm.country_code,
        organization: regForm.organization,
        title: regForm.title || '',
        phone: phoneComposed,
        website: websiteNormalized,
        address: regForm.address || '',
        password: regForm.password,
        language: language
      });
      setRegSuccess(true);
    } catch (error) {
      // Surface the most useful message available
      const backendDetail = error.response?.data?.detail;
      const httpStatus = error.response?.status;
      const netMsg = error.message || '';
      let msg;
      if (backendDetail) {
        msg = backendDetail;
      } else if (httpStatus) {
        msg = tr ? `Sunucu hatası (${httpStatus}).` : `Server error (${httpStatus}).`;
      } else if (netMsg.toLowerCase().includes('network')) {
        msg = tr
          ? 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin veya sayfayı yenileyip tekrar deneyin.'
          : 'Could not reach the server. Check your connection or hard-refresh the page and try again.';
      } else {
        msg = netMsg || (tr ? 'Bir hata oluştu' : 'An error occurred');
      }
      setRegError(msg);
      // Also log full context for debugging
      console.error('self-register failed:', { httpStatus, backendDetail, netMsg, url: `${API}/auth/self-register` });
    } finally {
      setRegLoading(false);
    }
  };

  // ISO-2 → dial code (keeps UX snappy when user picks a country).
  const ISO_TO_DIAL = {
    AF:'+93',AL:'+355',DZ:'+213',AD:'+376',AO:'+244',AR:'+54',AM:'+374',AU:'+61',
    AT:'+43',AZ:'+994',BH:'+973',BD:'+880',BY:'+375',BE:'+32',BZ:'+501',BJ:'+229',
    BT:'+975',BO:'+591',BA:'+387',BW:'+267',BR:'+55',BN:'+673',BG:'+359',BF:'+226',
    BI:'+257',CV:'+238',KH:'+855',CM:'+237',CA:'+1',CF:'+236',TD:'+235',CL:'+56',
    CN:'+86',CO:'+57',KM:'+269',CG:'+242',CD:'+243',CR:'+506',CI:'+225',HR:'+385',
    CU:'+53',CY:'+357',CZ:'+420',DK:'+45',DJ:'+253',EC:'+593',EG:'+20',SV:'+503',
    GQ:'+240',ER:'+291',EE:'+372',SZ:'+268',ET:'+251',FJ:'+679',FI:'+358',FR:'+33',
    GA:'+241',GM:'+220',GE:'+995',DE:'+49',GH:'+233',GR:'+30',GT:'+502',GN:'+224',
    GW:'+245',GY:'+592',HT:'+509',HN:'+504',HU:'+36',IS:'+354',IN:'+91',ID:'+62',
    IR:'+98',IQ:'+964',IE:'+353',IL:'+972',IT:'+39',JP:'+81',JO:'+962',KZ:'+7',
    KE:'+254',KI:'+686',KP:'+850',KR:'+82',KW:'+965',KG:'+996',LA:'+856',LV:'+371',
    LB:'+961',LS:'+266',LR:'+231',LY:'+218',LI:'+423',LT:'+370',LU:'+352',MG:'+261',
    MW:'+265',MY:'+60',MV:'+960',ML:'+223',MT:'+356',MR:'+222',MU:'+230',MX:'+52',
    FM:'+691',MD:'+373',MC:'+377',MN:'+976',ME:'+382',MA:'+212',MZ:'+258',MM:'+95',
    NA:'+264',NR:'+674',NP:'+977',NL:'+31',NZ:'+64',NI:'+505',NE:'+227',NG:'+234',
    MK:'+389',NO:'+47',OM:'+968',PK:'+92',PW:'+680',PA:'+507',PG:'+675',PY:'+595',
    PE:'+51',PH:'+63',PL:'+48',PT:'+351',QA:'+974',RO:'+40',RU:'+7',RW:'+250',
    WS:'+685',SM:'+378',ST:'+239',SA:'+966',SN:'+221',RS:'+381',SC:'+248',SL:'+232',
    SG:'+65',SK:'+421',SI:'+386',SB:'+677',SO:'+252',ZA:'+27',SS:'+211',ES:'+34',
    LK:'+94',SD:'+249',SR:'+597',SE:'+46',CH:'+41',SY:'+963',TJ:'+992',TZ:'+255',
    TH:'+66',TL:'+670',TG:'+228',TO:'+676',TN:'+216',TR:'+90',TM:'+993',TV:'+688',
    UG:'+256',UA:'+380',AE:'+971',GB:'+44',US:'+1',UY:'+598',UZ:'+998',VU:'+678',
    VA:'+39',VE:'+58',VN:'+84',YE:'+967',ZM:'+260',ZW:'+263',PS:'+970',XK:'+383',
  };

  const selectCountry = (code) => {
    const dial = ISO_TO_DIAL[code];
    setRegForm((prev) => ({
      ...prev,
      country_code: code,
      // Only auto-set dial if user hasn't typed a phone number yet.
      phone_code: prev.phone_number ? prev.phone_code : (dial || prev.phone_code),
    }));
    setCountryOpen(false);
    setCountrySearch('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-sky-50">
      <Navbar scrolled={scrolled} />

      <section className="pt-32 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Loading */}
          {regOpen === null && (
            <div className="flex justify-center items-center py-32">
              <div className="animate-spin h-10 w-10 border-4 border-sky-600 border-t-transparent rounded-full" />
            </div>
          )}

          {/* Registration Closed */}
          {regOpen === false && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-gray-100 mb-6">
                <Shield className="h-12 w-12 text-gray-400" />
              </div>
              <h1 className="font-playfair text-3xl md:text-4xl text-[#0A3A5A] font-medium mb-3 tracking-tight">
                {tr ? 'Kayıt Geçici Olarak Kapalı' : 'Registration Temporarily Closed'}
              </h1>
              <p className="text-gray-500 text-lg mb-2">
                {tr
                  ? 'Blue Zone kayıt sistemi şu anda geçici olarak kapatılmıştır.'
                  : 'Blue Zone registration is temporarily unavailable.'}
              </p>
              <p className="text-gray-400 text-sm mb-8">
                {tr
                  ? 'Lütfen daha sonra tekrar ziyaret ediniz veya organizasyon ile iletişime geçiniz.'
                  : 'Please visit again later or contact the organization.'}
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-8 py-3 rounded-full font-medium transition-colors">
                {tr ? 'Ana Sayfaya Dön' : 'Back to Home'}
              </button>
            </div>
          )}

          {regOpen === true && (<>
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-sky-600 mb-4">
              <Shield className="h-12 w-12 text-white" />
            </div>
            <h1 className="font-playfair text-4xl md:text-5xl text-[#0A3A5A] font-medium mb-2 tracking-tight">
              {tr ? 'Blue Zone Kayıt' : 'Blue Zone Registration'}
            </h1>
            <p className="text-[#4A5568]">
              {tr ? 'Sisteme erişim yönteminizi seçin' : 'Choose your access method'}
            </p>
          </div>

          {/* Tab Selector */}
          <div className="flex rounded-2xl bg-gray-100 p-1 mb-6">
            <button
              data-testid="tab-unfccc"
              onClick={() => setActiveTab('unfccc')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'unfccc' ? 'bg-white shadow-md text-sky-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <KeyRound className="h-4 w-4" />
              {tr ? 'Kayıtlı Delege Girişi' : 'Registered Delegate Login'}
            </button>
            <button
              data-testid="tab-self-register"
              onClick={() => setActiveTab('self')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'self' ? 'bg-white shadow-md text-blue-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserPlus className="h-4 w-4" />
              {tr ? 'Yeni Başvuru' : 'New Application'}
            </button>
          </div>

          {/* ── Tab 1: UNFCCC ── */}
          {activeTab === 'unfccc' && (
            <Card className="border-0 shadow-2xl rounded-3xl">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-sky-600" />
                  {tr ? 'Delege E-posta Doğrulama' : 'Delegate Email Verification'}
                </CardTitle>
                <p className="text-gray-500 text-sm">
                  {tr ? 'Delege başvurusu sırasında kaydettiğiniz e-posta adresinizi giriniz' : 'Please enter your registered delegate email address'}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{tr ? 'E-posta Adresi' : 'Email Address'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      data-testid="unfccc-email-input"
                      id="email" type="email"
                      placeholder="example@unfccc.org"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={handleEmailBlur}
                      className="pl-10 rounded-xl"
                      disabled={emailVerified}
                    />
                  </div>
                  {emailError && (
                    <div className="flex items-start gap-2 text-red-600 text-sm mt-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{emailError}</span>
                    </div>
                  )}
                </div>

                {!emailVerified ? (
                  <Button
                    data-testid="unfccc-verify-btn"
                    onClick={handleVerifyEmail}
                    disabled={loading || !email}
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-full py-6 text-lg"
                  >
                    {loading
                      ? <><Loader className="h-5 w-5 mr-2 animate-spin" />{tr ? 'Doğrulanıyor...' : 'Verifying...'}</>
                      : (tr ? 'Doğrula' : 'Verify')}
                  </Button>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-blue-800 font-semibold mb-1">
                            {tr ? 'Doğrulama kodu e-postanıza gönderildi' : 'Code sent to your email'}
                          </p>
                          <p className="text-blue-700 text-sm">{tr ? 'Lütfen kodu giriniz' : 'Please enter the code'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Label htmlFor="code">{tr ? 'Doğrulama Kodu' : 'Verification Code'}</Label>
                        <Input
                          data-testid="verification-code-input"
                          id="code" type="text" placeholder="000000"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                          className="rounded-xl text-center text-2xl tracking-widest font-mono"
                          maxLength={6}
                        />
                        {codeError && <p className="text-red-600 text-sm mt-2">{codeError}</p>}
                      </div>
                      <div className="flex items-end">
                        <Button onClick={handleResendCode} disabled={!canResend} variant="outline" className="rounded-full px-6 py-6">
                          {tr ? 'Tekrar Gönder' : 'Resend'}
                        </Button>
                      </div>
                    </div>

                    {countdown > 0 && (
                      <p className="text-orange-600 text-sm text-center">
                        {countdown} {tr ? 'sn sonra tekrar gönderebilirsiniz' : 'sec to resend'}
                      </p>
                    )}

                    <Button
                      data-testid="confirm-code-btn"
                      onClick={handleVerifyCode}
                      disabled={loading || verificationCode.length !== 6}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-lg"
                    >
                      {loading ? <Loader className="h-5 w-5 animate-spin" /> : (tr ? 'Kodu Doğrula ve Giriş Yap' : 'Verify and Login')}
                    </Button>
                  </>
                )}

                <div className="border-t pt-4 text-center">
                  <p className="text-sm text-gray-500">
                    {tr ? 'Onaylı hesabınız var mı?' : 'Already have an approved account?'}{' '}
                    <a href="/giris" className="text-blue-600 hover:underline font-semibold">
                      {tr ? 'Şifreyle Giriş Yap' : 'Login with Password'}
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Tab 2: Self Register ── */}
          {activeTab === 'self' && (
            <Card className="border-0 shadow-2xl rounded-3xl">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                  {tr ? 'Yeni Başvuru' : 'New Application'}
                </CardTitle>
                <p className="text-gray-500 text-sm">
                  {tr
                    ? 'Bilgilerinizi doldurun. Başvurunuz Ultra Admin onayına gönderilecektir.'
                    : 'Fill in your details. Your application will be sent to Ultra Admin for approval.'}
                </p>
              </CardHeader>
              <CardContent>
                {regSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-12 w-12 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {tr ? 'Başvurunuz Alındı!' : 'Application Submitted!'}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {tr
                        ? 'Başvurunuz Ultra Admin\'e iletildi. Onaylandıktan sonra aşağıdaki bağlantıdan sisteme giriş yapabilirsiniz.'
                        : 'Your application has been sent to Ultra Admin. Once approved, you can login using the link below.'}
                    </p>
                    <a
                      href="/giris"
                      className="inline-block px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition"
                    >
                      {tr ? 'Giriş Sayfasına Git' : 'Go to Login Page'}
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSelfRegister} className="space-y-4" noValidate>
                    {regError && (
                      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{regError}</span>
                      </div>
                    )}

                    {/* Full Name */}
                    <div className="space-y-1">
                      <Label htmlFor="full_name">{tr ? 'Ad Soyad' : 'Full Name'} *</Label>
                      <Input
                        data-testid="reg-full-name"
                        id="full_name"
                        placeholder={tr ? 'Adınız Soyadınız' : 'Your Full Name'}
                        value={regForm.full_name}
                        onChange={(e) => { setRegForm({ ...regForm, full_name: e.target.value }); clearFieldError('full_name'); }}
                        className={`rounded-xl ${fieldErrors.full_name ? 'border-red-400 focus:border-red-400' : ''}`}
                      />
                      <FieldError msg={fieldErrors.full_name} data-testid="reg-full-name-error" />
                    </div>

                    {/* Email */}
                    <div className="space-y-1">
                      <Label htmlFor="reg_email">{tr ? 'E-posta Adresi' : 'Email Address'} *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <Input
                          data-testid="reg-email"
                          id="reg_email" type="email"
                          placeholder="email@example.com"
                          value={regForm.email}
                          onChange={(e) => { setRegForm({ ...regForm, email: e.target.value.trim() }); clearFieldError('email'); }}
                          onBlur={() => setRegEmailTouched(true)}
                          className={`pl-10 rounded-xl ${(fieldErrors.email || (regEmailTouched && regForm.email && !regEmailValid)) ? 'border-red-400 focus:border-red-400' : ''}`}
                          autoComplete="email"
                        />
                      </div>
                      <FieldError
                        data-testid="reg-email-error"
                        msg={fieldErrors.email || (regEmailTouched && regForm.email && !regEmailValid
                          ? (tr ? 'Geçerli bir e-posta giriniz (örn. ad@firma.com)' : 'Please enter a valid email (e.g. name@company.com)')
                          : null)}
                      />
                    </div>

                    {/* Country Dropdown */}
                    <div className="space-y-1" data-country-dropdown>
                      <Label>{tr ? 'Ülke' : 'Country'} *</Label>
                      <div className="relative">
                        <button
                          type="button"
                          data-testid="reg-country-btn"
                          onClick={() => setCountryOpen(!countryOpen)}
                          className={`w-full flex items-center justify-between border rounded-xl px-4 py-2.5 text-sm bg-white hover:border-gray-400 transition-colors ${fieldErrors.country_code ? 'border-red-400' : 'border-gray-200'}`}
                        >
                          <span className={regForm.country_code ? 'text-gray-900' : 'text-gray-400'}>
                            {regForm.country_code
                              ? getCountryLabel(regForm.country_code, language)
                              : (tr ? 'Ülke seçiniz...' : 'Select country...')}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${countryOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {countryOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                            <div className="p-2 border-b">
                              <Input
                                data-testid="country-search"
                                placeholder={tr ? 'Ülke ara...' : 'Search country...'}
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                className="rounded-lg text-sm"
                                autoFocus
                              />
                            </div>
                            <div className="max-h-56 overflow-y-auto">
                              {filteredCountries.length === 0 ? (
                                <p className="text-center text-gray-400 text-sm py-4">
                                  {tr ? 'Sonuç bulunamadı' : 'No results'}
                                </p>
                              ) : filteredCountries.map((c) => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => { selectCountry(c.code); clearFieldError('country_code'); }}
                                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center justify-between ${regForm.country_code === c.code ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                                >
                                  {c.label}
                                  {regForm.country_code === c.code && <Check className="h-4 w-4 text-blue-600" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <FieldError msg={fieldErrors.country_code} data-testid="reg-country-error" />
                    </div>

                    {/* Organization + Title */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="organization">{tr ? 'Kurum / Organizasyon' : 'Organization'} *</Label>
                        <Input
                          data-testid="reg-organization"
                          id="organization"
                          placeholder={tr ? 'Firma veya kurum adı' : 'Company or org name'}
                          value={regForm.organization}
                          onChange={(e) => { setRegForm({ ...regForm, organization: e.target.value }); clearFieldError('organization'); }}
                          className={`rounded-xl ${fieldErrors.organization ? 'border-red-400 focus:border-red-400' : ''}`}
                        />
                        <FieldError msg={fieldErrors.organization} data-testid="reg-organization-error" />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="title">{tr ? 'Görev / Unvan' : 'Title / Position'}</Label>
                        <Input
                          data-testid="reg-title"
                          id="title"
                          placeholder={tr ? 'Örn: Genel Müdür' : 'e.g. CEO'}
                          value={regForm.title}
                          onChange={(e) => setRegForm({ ...regForm, title: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    {/* Contact & address — optional but displayed in admin cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="phone">{tr ? 'Telefon' : 'Phone'}</Label>
                        <PhoneInput
                          codeValue={regForm.phone_code}
                          numberValue={regForm.phone_number}
                          onCodeChange={(c) => setRegForm({ ...regForm, phone_code: c })}
                          onNumberChange={(n) => {
                            setRegForm({ ...regForm, phone_number: n });
                            if (!regPhoneTouched && n) setRegPhoneTouched(true);
                          }}
                        />
                        {regPhoneTouched && regForm.phone_number && !regPhoneValid && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3.5 w-3.5" />
                            {tr
                              ? 'Telefon 7–15 rakam içermelidir (harf veya özel karakter kabul edilmez)'
                              : 'Phone must be 7–15 digits (no letters or symbols)'}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="website">{tr ? 'Web Sitesi' : 'Website'}</Label>
                        <Input
                          data-testid="reg-website"
                          id="website"
                          type="url"
                          inputMode="url"
                          placeholder="https://example.com"
                          value={regForm.website}
                          onChange={(e) => setRegForm({ ...regForm, website: e.target.value.trim() })}
                          onBlur={() => setRegWebsiteTouched(true)}
                          className={`rounded-xl ${regWebsiteTouched && regForm.website && !regWebsiteValid ? 'border-red-400 focus:border-red-400' : ''}`}
                          autoComplete="url"
                        />
                        {regWebsiteTouched && regForm.website && !regWebsiteValid && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3.5 w-3.5" />
                            {tr
                              ? 'Geçerli bir site giriniz (örn. firmaniz.com veya https://firmaniz.com)'
                              : 'Enter a valid site (e.g. yourcompany.com or https://yourcompany.com)'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="address">{tr ? 'Adres' : 'Address'}</Label>
                      <Input
                        data-testid="reg-address"
                        id="address"
                        placeholder={tr ? 'Cadde, şehir, ülke' : 'Street, city, country'}
                        value={regForm.address}
                        onChange={(e) => setRegForm({ ...regForm, address: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>

                    {/* Password */}
                    <div className="space-y-1">
                      <Label htmlFor="password">{tr ? 'Şifre' : 'Password'} *</Label>
                      <div className="relative">
                        <Input
                          data-testid="reg-password"
                          id="password"
                          type={showPass ? 'text' : 'password'}
                          placeholder={tr ? 'En az 8 karakter' : 'Min. 8 characters'}
                          value={regForm.password}
                          onChange={(e) => {
                            setRegForm({ ...regForm, password: e.target.value });
                            if (!passwordTouched) setPasswordTouched(true);
                            clearFieldError('password');
                          }}
                          onBlur={() => setPasswordTouched(true)}
                          className={`pr-10 rounded-xl ${fieldErrors.password ? 'border-red-400 focus:border-red-400' : ''}`}
                        />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-3 text-gray-400">
                          {showPass ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <FieldError msg={fieldErrors.password} data-testid="reg-password-error" />

                      {/* Password rules - show while typing */}
                      {(passwordTouched || regForm.password.length > 0) && (
                        <div className="bg-gray-50 rounded-xl p-3 mt-2 grid grid-cols-2 gap-1">
                          <PasswordRule ok={passwordRules.length} label={tr ? 'En az 8 karakter' : 'Min. 8 characters'} />
                          <PasswordRule ok={passwordRules.uppercase} label={tr ? 'Büyük harf' : 'Uppercase letter'} />
                          <PasswordRule ok={passwordRules.lowercase} label={tr ? 'Küçük harf' : 'Lowercase letter'} />
                          <PasswordRule ok={passwordRules.number} label={tr ? 'Rakam' : 'Number'} />
                          <PasswordRule ok={passwordRules.special} label={tr ? 'Özel karakter (!@#$...)' : 'Special char (!@#$...)'} />
                        </div>
                      )}

                      {/* On blur error */}
                      {passwordTouched && !allRulesOk && regForm.password.length > 0 && (
                        <p className="text-red-500 text-xs mt-1">
                          {tr ? 'Şifre tüm kurallara uymalıdır' : 'Password must meet all requirements'}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-1">
                      <Label htmlFor="password2">{tr ? 'Şifre Tekrar' : 'Confirm Password'} *</Label>
                      <div className="relative">
                        <Input
                          data-testid="reg-password2"
                          id="password2"
                          type={showPass2 ? 'text' : 'password'}
                          placeholder={tr ? 'Şifreyi tekrar girin' : 'Re-enter password'}
                          value={regForm.password2}
                          onChange={(e) => { setRegForm({ ...regForm, password2: e.target.value }); clearFieldError('password2'); }}
                          onBlur={() => setPass2Touched(true)}
                          className={`pr-10 rounded-xl ${(fieldErrors.password2 || (pass2Touched && regForm.password2 && !passwordsMatch)) ? 'border-red-400 focus:border-red-400' : ''}`}
                        />
                        <button type="button" onClick={() => setShowPass2(!showPass2)} className="absolute right-3 top-3 text-gray-400">
                          {showPass2 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <FieldError
                        data-testid="reg-password2-error"
                        msg={fieldErrors.password2 || (pass2Touched && regForm.password2 && !passwordsMatch
                          ? (tr ? 'Şifreler eşleşmiyor' : 'Passwords do not match')
                          : null)}
                      />
                      {pass2Touched && regForm.password2 && passwordsMatch && allRulesOk && (
                        <p className="text-blue-600 text-xs mt-1 flex items-center gap-1">
                          <Check className="h-3.5 w-3.5" />
                          {tr ? 'Şifreler eşleşiyor' : 'Passwords match'}
                        </p>
                      )}
                    </div>

                    <Button
                      data-testid="self-register-submit-btn"
                      type="submit"
                      disabled={regLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-lg mt-2"
                    >
                      {regLoading
                        ? <><Loader className="h-5 w-5 mr-2 animate-spin" />{tr ? 'Gönderiliyor...' : 'Submitting...'}</>
                        : (tr ? 'Başvuru Gönder' : 'Submit Application')}
                    </Button>

                    <div className="text-center text-sm text-gray-500">
                      {tr ? 'Zaten onaylı hesabınız var mı?' : 'Already approved?'}{' '}
                      <a href="/giris" className="text-blue-600 hover:underline font-semibold">
                        {tr ? 'Giriş Yap' : 'Login'}
                      </a>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          )}
          </>)}
        </div>
      </section>
    </div>
  );
};

export default BlueZoneRegistration;
